import { Mutable } from 'type-fest';

/**
 * Checks if a value is defined (not undefined).
 *
 * @template T - The type of the value passed in (can be any type).
 * @param {T | undefined} value - The value to check.
 * @returns {boolean} True if the value is defined; otherwise, false.
 */
export const isDefined: <T>(value: T | undefined) => value is T = <T>(value: T | undefined): value is T =>
  value !== undefined;

/* eslint-disable no-null/no-null */
/**
 * Checks if a value is neither undefined nor null.
 *
 * @template T - The inferred type of the value (can be any type).
 * @param {T} value - The value to check.
 * @returns {value is Exclude<T, null | undefined>} True if the value is neither undefined nor null.
 */
export const isDefinedNotNull: <T>(value: T) => value is Exclude<T, null | undefined> = <T>(
  value: T
): value is Exclude<T, null | undefined> => value !== null && value !== undefined;
/* eslint-enable no-null/no-null */

/**
 * Checks if a value is undefined.
 *
 * @template T - The type of the value passed in (can be any type).
 * @param {T | undefined} value - The value to check.
 * @returns {value is undefined} True if the value is undefined; otherwise, false.
 */
export const isUndefined: <T>(value: T | undefined) => value is undefined = <T>(
  value: T | undefined
): value is undefined => value === undefined;

/* eslint-disable no-null/no-null */
/**
 * Checks if a value is undefined or null.
 *
 * @template T - The type of the value passed in (can be any type).
 * @param {T} value - The value to check.
 * @returns {value is Extract<T, null | undefined>} True if the value is undefined or null; otherwise, false.
 */
export const isUndefinedOrNull: <T>(value: T) => value is Extract<T, null | undefined> = <T>(
  value: T
): value is Extract<T, null | undefined> => value === null || value === undefined;
/* eslint-enable no-null/no-null */

/**
 * Checks if an object, string, or array is empty.
 *
 * @template T - The type of the value passed in (can be any type).
 * @param {T} value - The object, string, or array to check.
 * @returns {boolean} True if the value is an object, string, or array and is empty; otherwise, false (including if the
 *   value is of some other type or is null or undefined).
 */
export const isEmpty: <T = unknown>(value: T) => boolean = <T = unknown>(value: T): boolean => {
  // eslint-disable-next-line no-null/no-null
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return (value as { length: number }).length === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false;
};

/**
 * Checks if a value is undefined, null, or empty (for strings, arrays, and objects) and checks for NaN with numbers.
 *
 * @template T - The type of the value passed in (can be any type).
 * @param {T} value - The value to check.
 * @returns {value is T & (null | undefined | '' | [] | number)} True if the value is undefined, null, empty, or NaN;
 *   otherwise, false (including if the value is of some other type).
 */
export const isUndefinedOrNullOrEmpty: <T>(value: T) => value is T & (null | undefined | '' | [] | number) = <T>(
  value: T
): value is T & (null | undefined | '' | [] | number) => {
  /* For the above return type, since there isn't a specific literal type for NaN in TypeScript, including 'number'
     allows the predicate to remain valid when a numeric NaN is passed. */

  if (isUndefinedOrNull(value)) {
    return true;
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'number') {
    return isNaN(value);
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false;
};

/**
 * Checks if a value is defined, not null, and not empty (for strings, arrays, and objects)
 *  and checks for !NaN with numbers.
 *
 * @template T - The type of the value passed in (can be any type).
 * @param {T} value - The value to check.
 * @returns {value is Exclude<T, null | undefined> & (string | readonly unknown[] | number | object)}
 *  True if the value is defined, not null, not empty, and not NaN; otherwise, false (including if the
 *   value is of some other type).
 */
export const isDefinedNotNullNotEmpty: <T>(
  value: T
) => value is Exclude<T, null | undefined> & (string | readonly unknown[] | number | object) = <T>(
  value: T
): value is Exclude<T, null | undefined> & (string | readonly unknown[] | number | object) => {
  if (isUndefinedOrNull(value)) {
    return false;
  }

  if (typeof value === 'number') {
    return !isNaN(value);
  }

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }

  return false;
};

/**
 * Performs a deep clone of an object.
 * https://javascript.plainenglish.io/deep-clone-an-object-and-preserve-its-type-with-typescript-d488c35e5574
 *
 * @param source The object to clone.
 * @param preservePropertyDescriptor Whether to preserve the property descriptor. To see what this contains, refer to:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/getOwnPropertyDescriptor
 * @param excludedProperties A string or array of strings representing property names you do not want to copy into (at
 * any level) of the cloned object.
 *
 * @returns string A Mutable version of the source object.
 */
export const deepCopy: Function = <T>(
  source: T,
  preservePropertyDescriptor: boolean = false,
  excludedProperties: string[]
): Mutable<T> | undefined => {
  if (isUndefined(source)) {
    return undefined;
  }

  return Array.isArray(source)
    ? source.map((item: T): Mutable<T> => deepCopy(item, preservePropertyDescriptor, excludedProperties))
    : source instanceof Set
      ? new Set(source) // This works b/c Sets must contain primitives to be useful
      : source instanceof Date
        ? new Date(source.getTime())
        : source && typeof source === 'object'
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? Object.getOwnPropertyNames(source).reduce((obj: any, prop: string): Mutable<T> => {
              if (excludedProperties?.includes(prop)) {
                // skip property
                return obj;
              } else {
                if (preservePropertyDescriptor) {
                  const descriptor: PropertyDescriptor | undefined = Object.getOwnPropertyDescriptor(source, prop);
                  if (descriptor !== undefined) {
                    Object.defineProperty(obj, prop, descriptor);
                  }
                }
                // @ts-ignore: To access the object property with a variable
                obj[prop] = deepCopy(source[prop], preservePropertyDescriptor, excludedProperties);
                return obj;
              }
            }, Object.create(Object.getPrototypeOf(source)))
          : (source as Mutable<T>);
};
